import { useAppSelector } from '@/store/hooks';
import { PositionControl } from './PositionControl';
import type { ImageProps } from '@builder4/shared/components/Image';
import type { PositionAnchorsState, StylingProps } from '@builder4/shared/types/layout';
import type { PositionControlProps } from '@/types/layout';
import { getElementDimensionsWithPadding } from '@/utils/numToMeasurement';
import { useEditor, useNode } from '@craftjs/core';
import { selectScale } from '@/store/zoom/selectors';
import { getNodeTopLeftInParentElement } from '@/utils/helperFunc';
import { useBreakpointAwareProp } from '@/hooks/useBreakpointAwareProp';

const NodePositionControlSettingsV2 = ({ value }: { value: StylingProps['position'] }) => {
  const {
    actions: { setProp },
    nodeProps,
    id,
    node,
    parent,
    displayName,
  } = useNode((node) => ({
    nodeProps: node.data.props,
    node: node,
    id: node.id,
    parent: node.data.parent,
    displayName: node.data.displayName,
  }));
  const { actions, query } = useEditor();
  const scale = useAppSelector(selectScale);
  const { getBPProp, setBPProp } = useBreakpointAwareProp(displayName);

  const currentTop = getBPProp(nodeProps, 'top');
  const currentRight = getBPProp(nodeProps, 'right');
  const currentBottom = getBPProp(nodeProps, 'bottom');
  const currentLeft = getBPProp(nodeProps, 'left');
  const currentPositionAnchorsState = getBPProp(nodeProps, 'positionAnchorsState');

  const updateMirroredAnchor = (
    side: 'top' | 'left' | 'right' | 'bottom',
    props: Partial<StylingProps>,
  ) => {
    const parentNode = query.node(parent as string).get();
    if (!parentNode?.dom) return;

    const currentPosition = getNodeTopLeftInParentElement(parentNode, node, scale);
    switch (side) {
      case 'left':
        setBPProp(props, 'right', `${currentPosition.right}px`);
        break;
      case 'right':
        setBPProp(props, 'left', `${currentPosition.left}px`);
        break;
      case 'top':
        setBPProp(props, 'bottom', `${currentPosition.bottom}px`);
        break;
      case 'bottom':
        setBPProp(props, 'top', `${currentPosition.top}px`);
        break;
    }
  };

  const handleOnChangeAnchorPositions = (side: keyof PositionControlProps, value: string) => {
    const sanitized = value.trim().replace('px', '');
    const convertedToNum = Number(sanitized);
    const isValid = !isNaN(convertedToNum);
    actions.history.ignore().setProp(id, (props: Partial<StylingProps>) => {
      if (sanitized === '') {
        if (side === 'top' || side === 'left') {
          setBPProp(props, side, '0px');
        } else if (side === 'right' || side === 'bottom') {
          setBPProp(props, side, undefined);
        }
      } else if (isValid) {
        setBPProp(props, side, `${convertedToNum}px`);
      }
      updateMirroredAnchor(side, props);
    });
  };

  const normalizePositionValue = (
    value: string,
    side: 'top' | 'left' | 'right' | 'bottom',
  ): string | undefined => {
    const sanitized = value.trim().replace('px', '');

    if (sanitized === '') {
      return ['top', 'left'].includes(side) ? '0px' : undefined;
    }

    const numValue = Number(sanitized);
    return !isNaN(numValue) ? `${numValue}px` : undefined;
  };

  const updatePosition = (
    side: 'top' | 'left' | 'right' | 'bottom',
    value: string,
    skipHistory = false,
  ) => {
    const normalizedValue = normalizePositionValue(value, side);

    const actionMethod = skipHistory ? actions.history.ignore().setProp : actions.setProp;

    actionMethod(id, (props: StylingProps) => {
      setBPProp(props, side, normalizedValue);
      updateMirroredAnchor(side, props);
    });
  };

  const handleOnChangeAnchorPositionsComplete = (
    side: 'top' | 'left' | 'right' | 'bottom',
    value: string,
    initialValue: string,
  ) => {
    // Update without history (for initial/preview)
    updatePosition(side, initialValue, true);

    // Update with history (for final state)
    updatePosition(side, value);
  };

  const updateWidthBasedOnActiveAnchors = (props: ImageProps) => {
    const { width } = getElementDimensionsWithPadding(node.dom as HTMLElement);
    const positionAnchorsState = getBPProp(props, 'positionAnchorsState');
    const isLeftActive = positionAnchorsState.enableLeft;
    const isRightActive = positionAnchorsState.enableRight;

    if (isLeftActive && isRightActive) {
      setBPProp(props, 'width', 'auto');
    } else {
      setBPProp(props, 'width', `${width}px`);
    }
  };
  const updateHeightBasedOnActiveAnchors = (props: ImageProps) => {
    const { height } = getElementDimensionsWithPadding(node.dom as HTMLElement);
    const positionAnchorsState = getBPProp(props, 'positionAnchorsState');
    const isTopActive = positionAnchorsState.enableTop;
    const isBottomActive = positionAnchorsState.enableBottom;

    if (isTopActive && isBottomActive) {
      setBPProp(props, 'height', 'auto');
    } else {
      setBPProp(props, 'height', `${height}px`);
    }
  };

  const toggleAnchorPoints = (side: keyof PositionAnchorsState) => {
    if (value === 'sticky') {
      setProp((props: ImageProps) => {
        if (side === 'enableTop') {
          //sticky do not allow other sides
          const positionAnchorsState = getBPProp(props, 'positionAnchorsState');
          const newPositionAnchorsState = {
            ...positionAnchorsState,
            [side]: !positionAnchorsState[side],
          };
          setBPProp(props, 'positionAnchorsState', newPositionAnchorsState);
        }
      }, 300);
    } else {
      setProp((props: ImageProps) => {
        const positionAnchorsState = getBPProp(props, 'positionAnchorsState');
        const newPositionAnchorsState = {
          ...positionAnchorsState,
          [side]: !positionAnchorsState[side],
        };
        setBPProp(props, 'positionAnchorsState', newPositionAnchorsState);

        setBPProp(props, 'top', currentTop);
        setBPProp(props, 'right', currentRight);
        setBPProp(props, 'bottom', currentBottom);
        setBPProp(props, 'left', currentLeft);

        if (side === 'enableLeft' || side === 'enableRight') {
          updateWidthBasedOnActiveAnchors(props);
        } else if (side === 'enableTop' || side === 'enableBottom') {
          updateHeightBasedOnActiveAnchors(props);
        }
      }, 300);
    }
  };

  const togglePositionControlSquare = () => {
    setProp((props: ImageProps) => {
      if (value === 'sticky') {
        setBPProp(props, 'positionAnchorsState', {
          enableTop: true,
          enableRight: false,
          enableBottom: false,
          enableLeft: false,
          enabled: false,
        });
      } else {
        const positionAnchorsState = getBPProp(props, 'positionAnchorsState');
        if (positionAnchorsState.enabled) {
          setBPProp(props, 'positionAnchorsState', {
            enableTop: false,
            enableRight: false,
            enableBottom: false,
            enableLeft: false,
            enabled: false,
          });
        } else {
          setBPProp(props, 'positionAnchorsState', {
            enableTop: true,
            enableRight: true,
            enableBottom: true,
            enableLeft: true,
            enabled: true,
          });
        }
      }
    }, 300);
  };
  return (
    <PositionControl
      positionAnchors={currentPositionAnchorsState}
      position={{
        top: currentTop,
        right: currentRight,
        bottom: currentBottom,
        left: currentLeft,
      }}
      onClick={toggleAnchorPoints}
      onChange={handleOnChangeAnchorPositions}
      onChangeComplete={handleOnChangeAnchorPositionsComplete}
      onClickPositionControlSquare={togglePositionControlSquare}
      className=""
      text=""
    />
  );
};

export default NodePositionControlSettingsV2;
